import type { PerformanceAgent } from '@/shared/api/adminApi';

/** Seconds → "HH:MM:SS" (zero-padded, hours can exceed 24). */
export function fmtHms(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(h)}:${p(m)}:${p(sec)}`;
}

/** Whole-percent conversion a/b, 0 when the denominator is 0. */
export function pct(numerator: number, denominator: number): number {
  if (!denominator) return 0;
  return Math.round((numerator / denominator) * 100);
}

/**
 * Hebrew "how long in this status" from an ISO timestamp to now: "כ-2 שעות" /
 * "כ-15 דקות" / "עכשיו". Rounds to hours once past 60 minutes.
 */
export function statusDuration(sinceIso: string | null, now: number = Date.now()): string {
  if (!sinceIso) return '';
  const then = new Date(sinceIso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.floor((now - then) / 60000);
  if (mins < 1) return 'עכשיו';
  if (mins < 60) return `כ-${mins} דקות`;
  const hours = Math.round(mins / 60);
  return `כ-${hours} שעות`;
}

/** First two "letters" of a name (Hebrew-friendly): initials from up to 2 words. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2);
  return (parts[0][0] ?? '') + (parts[1][0] ?? '');
}

/** Deterministic hue (0-359) from a name, for a per-agent avatar color. */
export function hueFromName(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
}

/** Hebrew label for an agent presence status. */
export const AGENT_STATUS_LABEL: Record<string, string> = {
  available: 'זמין',
  oncall: 'מטפל בלקוח',
  acw: 'סיכום שיחה',
  break: 'הפסקה',
  lunch: 'ארוחת צהריים',
  training: 'הדרכה',
  paperwork: 'עבודת ניירת',
  offline: 'לא פעיל',
};

export function agentStatusLabel(status: string): string {
  return AGENT_STATUS_LABEL[status] ?? status;
}

/** Flatten the agents table to CSV rows (header + data) for client-side export. */
export function agentsToCsv(agents: PerformanceAgent[]): string {
  const header = [
    'שם', 'שלוחה', 'סטטוס', 'שיחות יוצאות', 'אחוז יוצאות', 'ידני', 'נוהלו', 'סגורות',
    'זמן בחיוג', 'זמן בשיחה', 'זמן משולב',
  ];
  const rows = agents.map((a) => [
    a.name,
    a.extension ?? '',
    agentStatusLabel(a.status),
    a.outbound,
    a.outbound_pct,
    a.manual,
    a.handled,
    a.deals,
    fmtHms(a.dialing_sec),
    fmtHms(a.talk_sec),
    fmtHms(a.combined_sec),
  ]);
  return [header, ...rows]
    .map((r) => r.map((c) => {
      const v = String(c ?? '');
      // Quote cells containing separators/quotes/newlines.
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(','))
    .join('\n');
}
